import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Alert, 
  Container,
  CircularProgress,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import { 
  CloudUploadOutlined, 
  CheckCircle, 
  Cancel,
  ArrowBack,
  FileUpload
} from '@mui/icons-material';
import api from '../api';
import { uiPT } from '../i18n';
import Card from '../components/Card';

interface ResubmitFormData {
  name: string;
}

const schema = yup.object({
  name: yup.string().required('Nome é obrigatório').min(2, 'Nome deve ter pelo menos 2 caracteres'),
}) as yup.ObjectSchema<ResubmitFormData>;

interface Client {
  trackingCode: string;
  name: string;
  status: string;
  rejectionReason?: string;
  rejectedDocuments?: {
    selfie?: boolean;
    modalityPhoto?: boolean;
    driverLicense?: boolean;
    idCardFront?: boolean;
    idCardBack?: boolean;
    idCardPdf?: boolean;
    caeDocument?: boolean;
    taxClearanceDocument?: boolean;
  };
}

export default function ResubmitPage() {
  const { trackingCode } = useParams<{ trackingCode: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clientLoading, setClientLoading] = useState(true);
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>({});
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File>>({});

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ResubmitFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    if (trackingCode) {
      fetchClient();
    }
  }, [trackingCode]);

  const fetchClient = async () => {
    try {
      const response = await api.get(`/tracking/${trackingCode}`);
      const clientData = response.data.data;
      setClient(clientData);
      setValue('name', clientData.name);
    } catch (err: any) {
      setError('Cliente não encontrado');
    } finally {
      setClientLoading(false);
    }
  };

  const handleFileChange = (docType: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFiles(prev => ({
        ...prev,
        [docType]: file
      }));
      setUploadedFiles(prev => ({
        ...prev,
        [docType]: file.name
      }));
    }
  };

  const removeFile = (docType: string) => {
    setSelectedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[docType];
      return newFiles;
    });
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[docType];
      return newFiles;
    });
  };

  const onSubmit = async (data: ResubmitFormData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('name', data.name);

      // Add selected files
      Object.entries(selectedFiles).forEach(([docType, file]) => {
        formData.append(docType, file);
      });

      await api.put(`/client/resubmit/${trackingCode}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccess('Documentos reenviados com sucesso!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao reenviar documentos');
    } finally {
      setLoading(false);
    }
  };

  if (clientLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
        <Container maxWidth="md">
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" color="error">
              Cliente não encontrado
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  if (!client.rejectedDocuments) {
    return (
      <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
        <Container maxWidth="md">
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5">
              Nenhum documento precisa ser reenviado
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  const rejectedDocuments = Object.keys(client.rejectedDocuments).filter(
    key => client.rejectedDocuments![key as keyof typeof client.rejectedDocuments]
  );

  return (
    <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/tracking')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {uiPT.resubmit.resubmitTitle}
            </Typography>
          </Box>

          {/* Resubmit Card */}
          <Card sx={{ p: 4 }}>
            <Stack spacing={4}>
              {/* Icon Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (theme) => theme.shadows[3],
                  }}
                >
                  <FileUpload sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Box>

              {/* Client Info */}
              <Card variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Cliente: {client.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Código: {client.trackingCode}
                  </Typography>
                  {client.rejectionReason && (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      <Typography variant="body2">
                        <strong>Motivo da rejeição:</strong> {client.rejectionReason}
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </Card>

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Nome"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        disabled={loading}
                      />
                    )}
                  />

                  {/* Rejected Documents Section */}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      {uiPT.resubmit.rejectedDocuments}:
                    </Typography>

                    <Stack spacing={2}>
                      {rejectedDocuments.map((docType) => (
                        <Card key={docType} variant="outlined" sx={{ p: 3 }}>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {getDocumentLabel(docType)}
                              </Typography>
                              <Chip label="Obrigatório" color="error" size="small" />
                            </Box>

                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<CloudUploadOutlined />}
                              fullWidth
                              sx={{ justifyContent: 'center' }}
                            >
                              {uiPT.resubmit.selectFile}
                              <input
                                type="file"
                                name={docType}
                                accept={getFileAccept(docType)}
                                hidden
                                onChange={handleFileChange(docType)}
                                required
                              />
                            </Button>

                            {uploadedFiles[docType] && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                                <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                  {uploadedFiles[docType]}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile(docType)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <Cancel sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Stack>
                        </Card>
                      ))}
                    </Stack>
                  </Box>

                  {/* Alerts */}
                  {error && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      {success}
                    </Alert>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || rejectedDocuments.length === 0}
                    sx={{
                      height: 48,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}
                  >
                    {loading ? 'Enviando...' : 'Reenviar Todos os Documentos'}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}

function getDocumentLabel(docType: string): string {
  const labels: Record<string, string> = {
    selfie: uiPT.dashboard.selfie,
    modalityPhoto: uiPT.dashboard.modalityPhoto,
    driverLicense: uiPT.dashboard.driverLicense,
    idCardFront: uiPT.dashboard.idCardFront,
    idCardBack: uiPT.dashboard.idCardBack,
    idCardPdf: uiPT.dashboard.idCardPdf,
    caeDocument: 'Código de Atividade Económica (CAE)',
    taxClearanceDocument: 'Certidão de Não Dívida - Autoridade Tributária',
  };
  return labels[docType] || docType;
}

function getFileAccept(docType: string): string {
  if (docType === 'idCardPdf' || docType === 'caeDocument' || docType === 'taxClearanceDocument') {
    return 'application/pdf';
  }
  return 'image/*';
} 